/**
 * S3 Utilities for NABL Application
 * Handles signed URL generation for files stored in form153labdetails_new
 */

/**
 * Check if a string is just a filename (no path or URL)
 * @param {string} filePath - The file path to check
 * @returns {boolean} - True if it's just a filename
 */
export const isJustFilename = (filePath) => {
    if (!filePath) return false;
    
    // Check if it's already a full URL (http/https)
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return false;
    }
    
    // Check if it's already a signed URL (contains X-Amz-Algorithm)
    if (filePath.includes('X-Amz-Algorithm')) {
        return false;
    }
    
    // Check if it contains path separators (live/, applications/, etc.)
    if (filePath.includes('/') || filePath.includes('\\')) {
        return false;
    }
    
    // If it's just a filename without path, return true
    return true;
};

/**
 * Determine the correct S3 path for a filename based on its format
 * @param {string} filename - The filename to analyze
 * @returns {string} - The S3 key with live/ path only
 */
const determineS3Path = (filename) => {
    if (!filename) return '';
    
    // All files are stored in live/ directory only
    console.log(`📁 Using live/ path for file: ${filename}`);
    return `live/${filename}`;
};

/**
 * Generate S3 signed URL for a filename
 * @param {string} filename - The filename to generate signed URL for
 * @param {number} expiresIn - Expiration time in seconds (default: 48 hours)
 * @returns {Promise<string|null>} - The signed URL or null if failed
 */
export const generateSignedUrlForFilename = async (filename, expiresIn = 'never') => {
    try {
        console.log(`🔗 Generating signed URL for filename: ${filename}`);
        
        // Get S3 path (only live/ path now)
        const s3Key = determineS3Path(filename);
        
        console.log(`🔍 Using S3 path: ${s3Key}`);
        
        // Try GET method first (URL parameter method)
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/files/url/${encodeURIComponent(s3Key)}?expiresIn=${expiresIn}`
            );
            const result = await response.json();
            
            if (result.success && result.data && result.data.url) {
                console.log(`✅ GET method: Successfully generated signed URL for ${filename} using path: ${s3Key}`);
                return result.data.url;
            }
        } catch (getError) {
            console.warn(`⚠️  GET method failed for ${filename} with path ${s3Key}:`, getError.message);
        }
        
        // Fallback to POST method (JSON body method)
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/files/signed-url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fileKey: s3Key,
                    expiresIn: 'never'
                })
            });
            
            const result = await response.json();
            
            if (result.success && result.signedUrl) {
                console.log(`✅ POST method: Successfully generated signed URL for ${filename} using path: ${s3Key}`);
                return result.signedUrl;
            } else {
                console.warn(`⚠️  POST method failed for ${filename} with path ${s3Key}:`, result.error);
            }
        } catch (postError) {
            console.warn(`⚠️  POST method failed for ${filename} with path ${s3Key}:`, postError.message);
        }
        
        console.error(`❌ Failed to generate signed URL for ${filename} using path: ${s3Key}`);
        return null;
    } catch (error) {
        console.error(`❌ Error generating signed URL for ${filename}:`, error);
        return null;
    }
};

/**
 * Process file data from form153labdetails_new and generate signed URLs if needed
 * @param {string} filePath - The file path from database
 * @param {string} fieldName - The field name for logging
 * @returns {Promise<Object>} - File object with signed URL if applicable
 */
export const processForm153FileData = async (filePath, fieldName = 'file') => {
    if (!filePath) return null;
    
    const displayName = extractFileName(filePath);
    let signedUrl = null;
    let fileKey = filePath;
    
    // Check if it's just a filename and generate signed URL
    if (isJustFilename(filePath)) {
        console.log(`📁 Processing form153 filename for ${fieldName}: ${filePath}`);
        signedUrl = await generateSignedUrlForFilename(filePath);
        fileKey = `live/${filePath}`;
    } else {
        console.log(`📁 Processing full path for ${fieldName}: ${filePath}`);
    }
    
    return {
        fileName: displayName,
        key: fileKey,
        originalName: displayName,
        signedUrl: signedUrl,
        storage: 'S3',
        isForm153Data: isJustFilename(filePath) // Flag to indicate this came from form153 filename
    };
};

/**
 * Extract filename from full path (handles various path formats)
 * @param {string} fullPath - The full file path
 * @returns {string|null} - The extracted filename
 */
export const extractFileName = (fullPath) => {
    if (!fullPath) return null;

    // Handle different path separators and extract the actual filename
    const pathParts = fullPath.split(/[/\\]/);
    const lastPart = pathParts[pathParts.length - 1];

    // If the filename contains timestamp and hash, extract the original name
    // Pattern: timestamp_hash_originalname.ext (e.g., "1758096638217_78b8f5e65409d0fc_sample.pdf")
    const match = lastPart.match(/^\d+_[a-f0-9]+_(.+)$/);
    if (match) {
        return match[1]; // Return the original filename part (e.g., "sample.pdf")
    }

    return lastPart; // Return as-is if no pattern match
};

/**
 * Batch process multiple form153 file fields
 * @param {Object} data - Object containing file fields from form153labdetails_new
 * @param {Array<string>} fileFields - Array of field names to process
 * @returns {Promise<Object>} - Object with processed file data
 */
export const batchProcessForm153Files = async (data, fileFields) => {
    const processedFiles = {};
    
    for (const fieldName of fileFields) {
        if (data[fieldName]) {
            console.log(`🔄 Processing ${fieldName}...`);
            processedFiles[fieldName] = await processForm153FileData(data[fieldName], fieldName);
        }
    }
    
    const processedCount = Object.keys(processedFiles).length;
    const signedUrlCount = Object.values(processedFiles).filter(file => file?.signedUrl).length;
    
    console.log(`📊 Batch processing complete: ${processedCount} files processed, ${signedUrlCount} signed URLs generated`);
    
    return processedFiles;
};

/**
 * Enhanced file view handler that automatically generates signed URLs for filename-only entries
 * This can be used to replace existing handleFileView functions
 * @param {string} fileKey - The file key/path/filename to view
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - Success status
 */
export const handleFileViewWithS3 = async (fileKey, options = {}) => {
    const { 
        showError = (msg) => console.error(msg),
        showLoading = () => {},
        hideLoading = () => {},
        expiresIn = 'never' // 48 hours default
    } = options;
    
    try {
        if (!fileKey) {
            showError('No file available to view');
            return false;
        }
        
        showLoading();
        let urlToOpen = null;
        
        // Check if it's just a filename and needs S3 signed URL generation
        if (isJustFilename(fileKey)) {
            console.log(`🔗 Generating signed URL for filename: ${fileKey}`);
            urlToOpen = await generateSignedUrlForFilename(fileKey, expiresIn);
        } else {
            // Handle full paths or existing URLs
            if (fileKey.startsWith('http://') || fileKey.startsWith('https://')) {
                // Already a full URL
                urlToOpen = fileKey;
            } else {
                // Try to get presigned URL for S3 key
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/files/url/${encodeURIComponent(fileKey)}?expiresIn=${expiresIn}`
                    );
                    const result = await response.json();
                    
                    if (result.success && result.data?.url) {
                        urlToOpen = result.data.url;
                    }
                } catch (error) {
                    console.warn('Failed to get presigned URL, trying direct construction:', error);
                    // Fallback: construct direct S3 URL
                    urlToOpen = `https://nabl7t.s3.ap-south-1.amazonaws.com/${fileKey}`;
                }
            }
        }
        
        if (urlToOpen) {
            window.open(urlToOpen, '_blank');
            console.log('✅ File opened successfully');
            return true;
        } else {
            throw new Error('Could not generate URL for file');
        }
        
    } catch (error) {
        console.error('❌ Error opening file:', error);
        showError(`Failed to open file: ${error.message}`);
        return false;
    } finally {
        hideLoading();
    }
};

/**
 * Process staff signature data (specifically for LabStaffSection)
 * @param {Object} staffData - Staff data containing SignatorySign field
 * @returns {Promise<Object>} - Processed staff data with signed URL
 */
export const processStaffSignatureData = async (staffData) => {
    if (!staffData || !staffData.SignatorySign) return null;
    
    const signatorySign = staffData.SignatorySign;
    
    // Check if it's already a signed URL
    if (signatorySign.includes('?X-Amz-Algorithm')) {
        return {
            fileName: extractFileName(signatorySign),
            filePath: signatorySign.split('?')[0].split('/').pop(),
            key: signatorySign.split('?')[0].split('/').pop(),
            originalName: extractFileName(signatorySign),
            signedUrl: signatorySign,
            storage: 'S3'
        };
    }
    
    // Check if it's just a filename
    if (isJustFilename(signatorySign)) {
        console.log(`🔄 Processing staff signature filename: ${signatorySign}`);
        const signedUrl = await generateSignedUrlForFilename(signatorySign);
        
        return {
            fileName: signatorySign,
            filePath: signatorySign,
            key: `live/${signatorySign}`,
            originalName: signatorySign,
            signedUrl: signedUrl,
            storage: 'S3',
            isForm153Data: true
        };
    }
    
    // Handle other formats
    return {
        fileName: extractFileName(signatorySign),
        filePath: signatorySign,
        key: signatorySign,
        originalName: extractFileName(signatorySign),
        signedUrl: null,
        storage: 'S3'
    };
};

/**
 * Process staff photo data and generate signed URL
 * @param {Object} staffData - Staff object with photo field
 * @returns {Promise<Object|null>} - Processed photo data with signed URL
 */
export const processStaffPhotoData = async (staffData) => {
    if (!staffData || !staffData.photo) return null;
    
    const photo = staffData.photo;
    
    // Check if it's already a signed URL
    if (photo.includes('?X-Amz-Algorithm')) {
        return {
            fileName: extractFileName(photo),
            filePath: photo.split('?')[0].split('/').pop(),
            key: photo.split('?')[0].split('/').pop(),
            originalName: extractFileName(photo),
            signedUrl: photo,
            storage: 'S3'
        };
    }
    
    // Check if it's just a filename
    if (isJustFilename(photo)) {
        console.log(`🔄 Processing staff photo filename: ${photo}`);
        const signedUrl = await generateSignedUrlForFilename(photo);
        
        return {
            fileName: photo,
            filePath: photo,
            key: `live/${photo}`,
            originalName: photo,
            signedUrl: signedUrl,
            storage: 'S3',
            isForm153Data: true
        };
    }
    
    // Handle other formats
    return {
        fileName: extractFileName(photo),
        filePath: photo,
        key: photo,
        originalName: extractFileName(photo),
        signedUrl: null,
        storage: 'S3'
    };
};

/**
 * Enhanced batch processing for equipment files (IQ, OQ, PQ)
 * @param {Array} equipmentList - Array of equipment objects
 * @param {Array<string>} fileFields - File fields to process (e.g., ['IQuploadFile', 'OQuploadFile', 'PQuploadFile'])
 * @returns {Promise<Array>} - Processed equipment list with signed URLs
 */
export const batchProcessEquipmentFiles = async (equipmentList, fileFields = ['IQuploadFile', 'OQuploadFile', 'PQuploadFile']) => {
    console.log(`🔄 Processing ${equipmentList.length} equipment items for file viewing...`);
    
    const processedEquipment = [];
    let totalSignedUrls = 0;
    
    for (const equipment of equipmentList) {
        const processedItem = { ...equipment };
        
        for (const fieldName of fileFields) {
            if (equipment[fieldName] && isJustFilename(equipment[fieldName])) {
                console.log(`🔗 Generating signed URL for ${fieldName}: ${equipment[fieldName]}`);
                const signedUrl = await generateSignedUrlForFilename(equipment[fieldName]);
                
                if (signedUrl) {
                    processedItem[`${fieldName}_signedUrl`] = signedUrl;
                    totalSignedUrls++;
                }
            }
        }
        
        processedEquipment.push(processedItem);
    }
    
    console.log(`📊 Equipment processing complete: ${totalSignedUrls} signed URLs generated for ${equipmentList.length} items`);
    return processedEquipment;
};

/**
 * Open a file using signed URL or fallback methods
 * @param {Object} fileData - File data object
 * @param {string} fallbackFilename - Fallback filename if file data is incomplete
 * @returns {Promise<boolean>} - True if successfully opened
 */
export const openFileWithSignedUrl = async (fileData, fallbackFilename = null) => {
    try {
        let urlToOpen = null;
        
        // Check if we already have a signed URL
        if (fileData?.signedUrl) {
            urlToOpen = fileData.signedUrl;
            console.log('📖 Opening file with existing signed URL');
        }
        // Check if we have a key and can generate a signed URL
        else if (fileData?.key) {
            console.log('🔗 Generating signed URL for key:', fileData.key);
            
            if (isJustFilename(fileData.key)) {
                urlToOpen = await generateSignedUrlForFilename(fileData.key);
            } else {
                // Try to get signed URL for full path
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/files/url/${encodeURIComponent(fileData.key)}`
                    );
                    const result = await response.json();
                    if (result.success && result.data?.url) {
                        urlToOpen = result.data.url;
                    }
                } catch (error) {
                    console.warn('Failed to generate signed URL for full path:', error);
                }
            }
        }
        // Fallback to filename if provided
        else if (fallbackFilename) {
            console.log('🔄 Using fallback filename:', fallbackFilename);
            urlToOpen = await generateSignedUrlForFilename(fallbackFilename);
        }
        
        if (urlToOpen) {
            window.open(urlToOpen, '_blank');
            console.log('✅ File opened successfully');
            return true;
        } else {
            console.error('❌ Could not generate URL for file');
            return false;
        }
    } catch (error) {
        console.error('❌ Error opening file:', error);
        return false;
    }
};

/**
 * Upload a file to S3 via backend API
 * @param {File} file - The file object to upload
 * @param {string} folder - Optional folder path (e.g., 'evidence', 'documents')
 * @returns {Promise<Object>} - Response with success status and file URL
 */
export const uploadFile = async (file, folder = 'evidence') => {
    try {
        if (!file) {
            throw new Error('No file provided');
        }

        console.log(`📤 Uploading file to S3: ${file.name}, size: ${file.size} bytes`);

        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        
        // Optional: add folder path for organization
        if (folder) {
            formData.append('folder', folder);
        }

        // Upload file via backend API
        const response = await fetch(`${process.env.REACT_APP_API_URL}/files/upload`, {
            method: 'POST',
            body: formData
        });

        const result = await response.json();

        if (result.success) {
            console.log('✅ File uploaded successfully:', result.data);
            return {
                success: true,
                url: result.data.url || result.data.location || result.data.key,
                key: result.data.key,
                fileName: result.data.fileName || file.name,
                data: result.data
            };
        } else {
            throw new Error(result.message || 'Upload failed');
        }

    } catch (error) {
        console.error('❌ Error uploading file to S3:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Default export with all utility functions
export default {
    isJustFilename,
    generateSignedUrlForFilename,
    processForm153FileData,
    extractFileName,
    batchProcessForm153Files,
    openFileWithSignedUrl,
    handleFileViewWithS3,
    processStaffSignatureData,
    processStaffPhotoData,
    batchProcessEquipmentFiles,
    uploadFile
};